// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Emails API Client
 *
 * Calls communications-service via advisor-bff (/api/integrations/email/**).
 * Read-only Gmail surface. Since the dashboard-calendar / relationship-emails
 * feature, the only UI consumer is the relationship Documents tab's Emails
 * card — the inbox page and client Emails tab were removed. The backend
 * keeps its wider surface (inbox listing, manual link/unlink); re-add
 * client methods here if a UI affordance returns.
 *
 * The connection-status endpoint is provider-level and returns the same
 * shape as the calendar one — we reuse that DTO from integrationsApi.
 */

import api from './advisorApi';
import type { CalendarConnectionStatusDTO, LinkMethod } from './integrationsApi';

export interface LinkedEmailClientDTO {
  clientProfileId: string;
  linkMethod: LinkMethod;
}

export interface EmailMessageDTO {
  messageId: string;
  externalId: string;
  threadId: string | null;
  subject: string | null;
  snippet: string | null;
  fromEmail: string | null;
  fromDisplay: string | null;
  toEmails: string[];
  ccEmails: string[];
  bccEmails: string[];
  receivedAt: string | null;
  unread: boolean;
  inSent: boolean;
  hasAttachments: boolean;
  labels: string[];
  linkedClients: LinkedEmailClientDTO[];
  /** Profiles this message is tag-linked to (Relationship:{uuid} tags). */
  linkedProfileIds: string[];
}

/** Spring Data Page shape — same as what the calendar surface returns elsewhere. */
export interface Page<T> {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export const emailsApi = {
  /**
   * Relationship feed: every message linked to any client of the profile
   * (from/to/cc/bcc auto-link, manual links, Client: tags) or tag-linked
   * to the profile itself (Relationship: tags), deduplicated, newest
   * first. Unknown / cross-tenant profiles yield an empty page.
   */
  async getMessagesForProfile(profileId: string, params?: {
    page?: number;
    size?: number;
  }): Promise<Page<EmailMessageDTO>> {
    const response = await api.get<Page<EmailMessageDTO>>(
      `/integrations/email/by-profile/${profileId}`,
      { params },
    );
    return response.data;
  },

  /**
   * Connection status — drives the hide-when-empty and re-auth-banner
   * branches. Same DTO shape as the calendar's connection-status because
   * connection state is provider-level, not capability-level.
   */
  async getConnectionStatus(): Promise<CalendarConnectionStatusDTO> {
    const response = await api.get<CalendarConnectionStatusDTO>(
      '/integrations/email/connection-status',
    );
    return response.data;
  },
};
